import { useErrorToast } from '@/lib/notify';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, ChartColumn, Cpu, Layers, Loader2, Search, ThermometerSun, X } from 'lucide-react';
import { fetchGlobalSearch, type GlobalSearchResult, type GlobalSearchType } from '@/api/search';
import { PDF_REPORTS } from '@/api/reports';
import { cn } from '@/lib/utils';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const REPORT_LIMIT = 6;

const TYPE_META: Record<GlobalSearchType, { label: string; icon: ReactNode; color: string; path: (id: string) => string }> = {
  powerplant: { label: 'Santraller', icon: <Building2 size={16} />, color: 'bg-amber-50 text-amber-600', path: (id) => `/dashboard/powerplants/${id}` },
  site: { label: 'Sahalar', icon: <Layers size={16} />, color: 'bg-emerald-50 text-emerald-600', path: (id) => `/dashboard/sites/${id}` },
  investor: { label: 'Yatırımcılar', icon: <Building2 size={16} />, color: 'bg-rose-50 text-rose-600', path: (id) => `/dashboard/investor/${id}` },
  inverter: { label: 'İnverterler', icon: <Cpu size={16} />, color: 'bg-blue-50 text-blue-600', path: (id) => `/dashboard/inverters/${id}` },
  sensor: { label: 'Sensörler', icon: <ThermometerSun size={16} />, color: 'bg-cyan-50 text-cyan-600', path: (id) => `/dashboard/sensors/${id}` },
  report: { label: 'Raporlar', icon: <ChartColumn size={16} />, color: 'bg-teal-50 text-teal-600', path: () => '/dashboard/reports' },
};
const TYPE_ORDER: GlobalSearchType[] = ['powerplant', 'site', 'investor', 'inverter', 'sensor', 'report'];

// Sunucudaki aramayla aynı Türkçe karakter eşlemesi.
const TR_FROM = 'İIıŞşĞğÜüÖöÇç';
const TR_TO = 'iiissgguuoocc';
function normalize(value: string): string {
  return Array.from(value, (ch) => {
    const i = TR_FROM.indexOf(ch);
    return i >= 0 ? TR_TO[i] : ch;
  }).join('').toLowerCase();
}

function searchReports(query: string): GlobalSearchResult[] {
  const term = normalize(query);
  return PDF_REPORTS
    .map((report) => {
      const title = normalize(report.title);
      const rank = title.startsWith(term) ? 0 : title.includes(term) ? 1 : normalize(report.description).includes(term) ? 2 : -1;
      return { report, rank };
    })
    .filter(({ rank }) => rank >= 0)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, REPORT_LIMIT)
    .map(({ report }) => ({ type: 'report', id: report.id, title: report.title, subtitle: report.description }));
}

function Highlight({ text, query }: { text: string; query: string }) {
  // normalize() karakter sayısını korur; indeks orijinal metinde de geçerli.
  const index = normalize(text).indexOf(normalize(query));
  if (index < 0 || !query) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark className="rounded-sm bg-amber-100 text-inherit">{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [remoteResults, setRemoteResults] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  useErrorToast(error ? 'Arama sonuçları alınamadı. Lütfen tekrar deneyin.' : '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const trimmed = query.trim();
  const ready = trimmed.length >= MIN_CHARS;
  const reportResults = useMemo(() => (ready ? searchReports(trimmed) : []), [trimmed, ready]);
  const results = useMemo(() => [...remoteResults, ...reportResults], [remoteResults, reportResults]);

  useEffect(() => {
    if (!ready) {
      setRemoteResults([]);
      setLoading(false);
      setError(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      fetchGlobalSearch(trimmed, controller.signal)
        .then((rows) => {
          setRemoteResults(rows);
          setError(false);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          setRemoteResults([]);
          setError(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed, ready]);

  // Ctrl/⌘+Shift+K veya yazı alanı dışındayken "/" ile açılır.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = !!target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
      if (((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k') || (e.key === '/' && !typing)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.select(), 50);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onEscape);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(results.length ? 0 : -1);
  }, [results]);

  // Sonuçları türe göre gruplar; klavye gezintisi için düz sırayı korur.
  const groups = useMemo(() => {
    const ordered = TYPE_ORDER.flatMap((type) => results.filter((r) => r.type === type));
    return {
      ordered,
      byType: TYPE_ORDER.map((type) => ({ type, items: ordered.filter((r) => r.type === type) })).filter((g) => g.items.length),
    };
  }, [results]);

  function select(result: GlobalSearchResult) {
    // Rapor sayfası prefillReportId ile ilgili raporun oluşturma penceresini açar.
    navigate(TYPE_META[result.type].path(result.id), result.type === 'report' ? { state: { prefillReportId: result.id } } : undefined);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!groups.ordered.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % groups.ordered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? groups.ordered.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      select(groups.ordered[activeIndex]);
    }
  }

  const activeId = activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined;

  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Santral, saha, yatırımcı, inverter, sensör veya rapor ara"
        title="Ara (/)"
        className="group flex h-9 w-28 items-center gap-2 rounded-xl border border-slate-100 bg-slate-100/60 px-3 shadow-sm transition-all hover:border-slate-200 hover:bg-white hover:shadow-md"
      >
        <Search size={14} className="shrink-0 text-slate-400 transition-colors group-hover:text-slate-600" />
        <span className="flex-1 truncate text-left text-xs font-semibold text-slate-400 group-hover:text-slate-500">Ara…</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-500" onClick={() => setOpen(false)} />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Genel arama"
            className="relative mx-4 w-full max-w-2xl overflow-hidden rounded-[32px] border border-white/50 bg-white/90 font-sans shadow-[0_30px_90px_-20px_rgba(0,0,0,0.3)] backdrop-blur-xl animate-in zoom-in-95 duration-300"
          >
            <div className="p-6">
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200/50 bg-white/60 p-2 shadow-sm transition-all focus-within:border-slate-300 focus-within:ring-4 focus-within:ring-slate-500/5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-white shadow-lg shadow-slate-200/50">
                  {loading ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
                </div>
                <input
                  ref={inputRef}
                  type="search"
                  role="combobox"
                  aria-label="Santral, saha, yatırımcı, inverter, sensör veya rapor ara"
                  aria-expanded={ready && groups.ordered.length > 0}
                  aria-controls={ready && groups.ordered.length > 0 ? listId : undefined}
                  aria-activedescendant={activeId}
                  aria-autocomplete="list"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Santral, saha, yatırımcı, inverter, sensör, rapor…"
                  className="min-w-0 flex-1 bg-transparent py-2 text-xl font-bold text-slate-800 outline-none placeholder:text-slate-400/60 [&::-webkit-search-cancel-button]:hidden"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="Aramayı temizle"
                    onClick={() => {
                      setQuery('');
                      inputRef.current?.focus();
                    }}
                    className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-black/5"
                  >
                    <X size={20} />
                  </button>
                )}
                <kbd title="Arama kısayolu" className="mr-2 shrink-0 rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">/</kbd>
              </div>
            </div>

            {ready && groups.ordered.length > 0 && (
              <div className="border-t border-slate-200/40 bg-slate-50/50 px-4 pb-4 pt-2">
                <div id={listId} role="listbox" aria-label="Arama sonuçları" className="max-h-[50vh] overflow-y-auto">
                  {groups.byType.map(({ type, items }) => (
                    <div key={type} role="group" aria-label={TYPE_META[type].label} className="py-1">
                      <p className="px-3 pb-1 pt-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{TYPE_META[type].label}</p>
                      {items.map((item) => {
                        const index = groups.ordered.indexOf(item);
                        return (
                          <div
                            key={`${item.type}-${item.id}`}
                            id={`${listId}-${index}`}
                            role="option"
                            aria-selected={index === activeIndex}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => select(item)}
                            className={cn(
                              'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 transition-colors',
                              index === activeIndex ? 'bg-slate-200/70' : 'hover:bg-slate-100',
                            )}
                          >
                            <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', TYPE_META[type].color)}>
                              {TYPE_META[type].icon}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-slate-800">
                                <Highlight text={item.title} query={trimmed} />
                              </span>
                              {item.subtitle && <span className="block truncate text-xs text-slate-500">{item.subtitle}</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
